import React, { memo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import Check from '@mui/icons-material/Check';
import Sell from '@mui/icons-material/Sell';
import type { TagOption } from './mappingScope';

interface TagFilterProps {
  options: TagOption[];
  selected: ReadonlySet<string>;
  onToggle: (tag: string) => void;
  onClear: () => void;
}

const TagFilter: React.FC<TagFilterProps> = ({ options, selected, onToggle, onClear }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>Tags</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.8 }}>Leave empty to include everything</Typography>
      {selected.size > 0 && (
        <Button
          size="small"
          onClick={onClear}
          sx={{ ml: 'auto', height: 28, px: 1.25, minWidth: 0, borderRadius: 3.5, fontSize: 12, fontWeight: 600, textTransform: 'none', color: 'primary.dark', '&:hover': { bgcolor: 'brand.container' } }}
        >
          Clear
        </Button>
      )}
    </Box>
    {options.length === 0 ? (
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No tags found in this spec.</Typography>
    ) : (
      <Box role="group" aria-label="Filter by tag" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {options.map(({ tag, count }) => {
          const on = selected.has(tag);
          const Icon = on ? Check : Sell;
          return (
            <ButtonBase
              key={tag}
              aria-pressed={on}
              onClick={() => onToggle(tag)}
              sx={{
                height: 32,
                pl: 1,
                pr: 1.25,
                gap: 0.75,
                borderRadius: 2,
                border: 1,
                borderColor: on ? 'transparent' : 'divider',
                bgcolor: on ? 'brand.container' : 'transparent',
                color: on ? 'brand.onContainer' : 'text.secondary',
                fontSize: 13,
                fontWeight: 500,
                '&:hover': { borderColor: on ? 'transparent' : 'primary.dark' },
                '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: 2 },
              }}
            >
              <Icon sx={{ fontSize: 16 }} />
              {tag}
              <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                {count}
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    )}
  </Box>
);

export default memo(TagFilter);
